// utils/offers.ts
import {config} from '@app/modules/config';
import {getNumberOfMonthsFromPeriod} from '@app/modules/offers/wording';
import type {
	AcqType,
	OffersAcquisitionResponse,
	OffersAcquisitionType,
	PlanType,
} from '@app/types/offersAcq';
import {
	getLegacyLocaleFromLocale,
	getSSGCountryFromLocale,
} from '@deezer/deezer-compatibility';
import type {TranslationKey} from 'globalTranslations';
import type {NextRouter} from 'next/router';

const PLAN_META: Record<
	PlanType,
	{id: number; color: string; trackKey: string}
> = {
	'annual-plan': {id: 1000001, color: '#a238ff', trackKey: 'annual-plan'},
	premium: {id: 1000001, color: '#a238ff', trackKey: 'premium'},
	duo: {id: 1000002, color: '#96f9f3', trackKey: 'duo'},
	family: {id: 1000006, color: '#ff673d', trackKey: 'family'},
	student: {id: 1005001, color: '#a238ff', trackKey: 'student'},
};

const BYPASS_CODE = ['202506_tiktok_acquisition', '2026_student_ambassador'];
const ANNUAL_PLAN_BOC = 'yearly_premium';
export const MAP_URL_WITH_OPERATION_CODE: Record<string, string> = {
	ambassador: '2026_student_ambassador',
};

const DEFAULT_TNC_URL = 'https://www.deezer.com/legal/cgu';

// Add an entry here for any new acquisitionType that needs its own T&C page,
// no other code change is needed. Falls back to DEFAULT_TNC_URL otherwise.
const TNC_URL_BY_ACQUISITION_TYPE: Partial<Record<AcqType, string>> = {
	ambassador:
		config.get('deezerStaticUrl') + '/pdfs/legal/tnc_student_ambassador.pdf',
};

export const getTncUrl = (acquisitionType: AcqType): string =>
	TNC_URL_BY_ACQUISITION_TYPE[acquisitionType] ?? DEFAULT_TNC_URL;

const isYearlyByPeriod = (o: OffersAcquisitionType) =>
	getPeriods(o).initial?.duration_type === 'Y';

const isAnnualPlanOffer = (o: OffersAcquisitionType) => {
	if (o.offer_id !== PLAN_META['annual-plan'].id) return false; // 1000001
	const boc = (o.business_operation_code ?? '').trim();
	return boc === ANNUAL_PLAN_BOC || (boc === '' && isYearlyByPeriod(o));
};

const now = () => new Date();

const hasActiveWindow = (o: OffersAcquisitionType, at = now()) => {
	const rawStart = (o as any)?.date_start?.date;
	const rawEnd = (o as any)?.date_end?.date;
	if (!rawStart || !rawEnd) return false;
	const start = new Date(rawStart);
	const end = new Date(rawEnd);
	return start <= at && end >= at;
};

const isSpecial = (o: OffersAcquisitionType) =>
	!!o.business_operation_code?.trim();

const matchesPlan = (plan: PlanType) => (o: OffersAcquisitionType) => {
	if (plan === 'annual-plan') return isAnnualPlanOffer(o);

	if (plan === 'premium') {
		return o.offer_id === PLAN_META.premium.id && !isAnnualPlanOffer(o);
	}

	return o.offer_id === PLAN_META[plan].id;
};

const notBypassed = (o: OffersAcquisitionType) =>
	!BYPASS_CODE.includes(o.business_operation_code ?? '');

/* ---------------------------------- */
/* Safe period accessors + classifiers*/
/* ---------------------------------- */

type Period =
	| {
			period: string;
			duration?: number;
			duration_type?: 'D' | 'M' | 'Y';
			iteration?: number;
			price?: string;
			price_total?: string;
			price_by_month?: string;
			reference?: string;
	  }
	| undefined;

const getPeriods = (o: OffersAcquisitionType) => {
	const coll = o.subscription_period_collection ?? ({} as any);
	const tryAndBuy: Period = coll?.try_and_buy;
	const initial: Period = coll?.initial;
	const renewal: Period = coll?.renewal;
	return {tryAndBuy, initial, renewal};
};

export const hasTry = (o: OffersAcquisitionType) => !!getPeriods(o).tryAndBuy;

const isDurationPromo = (o: OffersAcquisitionType) => {
	const {tryAndBuy, initial} = getPeriods(o);
	if (tryAndBuy) return false;
	return (initial?.duration ?? 1) > 1 && (initial?.iteration ?? 1) === 1;
};

const isIterationPromo = (o: OffersAcquisitionType) => {
	const {tryAndBuy, initial} = getPeriods(o);
	if (tryAndBuy) return false;
	return (initial?.iteration ?? 1) > 1;
};

// Only used in countdown logic (2–3 month "trial-like")
const isTrial2M3M = (o: OffersAcquisitionType) => {
	const d = getPeriods(o).tryAndBuy?.duration;
	return d === 2 || d === 3;
};

/* ---------------------------------- */
/* Pricing strategy helpers           */
/* ---------------------------------- */

const getDisplayMonthlyPrice = (o: OffersAcquisitionType) => {
	// For monthly plans, public display usually aligns with the steady state price
	// (renewal), even if there is a trial or intro promo.
	const {renewal, initial} = getPeriods(o);
	return (renewal?.price ?? initial?.price ?? '-') as string;
};

const getTrialInfo = (o: OffersAcquisitionType) => {
	const {tryAndBuy} = getPeriods(o);
	if (!tryAndBuy?.duration) return null;
	return {
		duration: tryAndBuy.duration,
		unit: tryAndBuy.duration_type ?? 'M',
	} as const;
};

const getIntroPromoInfo = (o: OffersAcquisitionType) => {
	const {initial, renewal} = getPeriods(o);
	return {
		duration: initial?.duration ?? 1,
		unit: (initial?.duration_type as 'D' | 'M' | 'Y') ?? 'M',
		iteration: initial?.iteration ?? 1,
		price: initial?.price ?? '-',
		renewalPrice: renewal?.price ?? '-',
	};
};

/* ---------------------------------- */
/* Promo months helper (fix)          */
/* ---------------------------------- */

const getPromoMonths = (o: OffersAcquisitionType): number | undefined => {
	const {tryAndBuy, initial} = getPeriods(o);

	if (tryAndBuy?.duration && tryAndBuy.duration_type === 'M') {
		return tryAndBuy.duration;
	}

	if (initial?.duration_type === 'M') {
		const dur = initial.duration ?? 1;
		const iter = initial.iteration ?? 1;

		if (iter > 1) return dur * iter;

		if (dur > 1) return dur;
	}

	return undefined;
};

/* ---------------------------------- */
/* Generic offer filter               */
/* ---------------------------------- */

const defaultOfferPredicate =
	(plan: PlanType, acquisitionType?: AcqType) => (o: OffersAcquisitionType) => {
		if (!matchesPlan(plan)(o)) return false;

		const operationCode = MAP_URL_WITH_OPERATION_CODE[acquisitionType || ''];
		if (!notBypassed(o) && o.business_operation_code !== operationCode) {
			return false;
		}

		if (plan === 'annual-plan') return true;

		const publicOk = !!o.public;
		const specialOk = hasActiveWindow(o) && isSpecial(o);
		return publicOk || specialOk;
	};

/**
 * Single place to fetch the "primary" matching offer(s) for a plan.
 */
export const selectOffers = (
	plan: PlanType,
	offers: OffersAcquisitionResponse,
	acquisitionType?: AcqType,
	predicate: (o: OffersAcquisitionType) => boolean = defaultOfferPredicate(
		plan,
		acquisitionType,
	),
) => offers.filter(predicate);

/* ---------------------- */
/* Small existing helpers */
/* ---------------------- */

function stripQueryAndHash(p: string) {
	return p.split('#')[0].split('?')[0];
}

export function resolvedPath(router: NextRouter) {
	let path = router.asPath ? stripQueryAndHash(router.asPath) : '';
	try {
		path = decodeURIComponent(path);
	} catch {
		/* noop */
	}

	if (path.includes('[')) {
		const {locale, query} = router;
		if (Array.isArray(query?.slug) && query.slug.length) {
			path = `/${locale || ''}/offers/${query.slug.join('/')}`.replace(
				/\/\//g,
				'/',
			);
		}
	}
	return path || '/';
}

/* --------------- */
/* Public API	  */
/* --------------- */

export const getPlanId = (plan: PlanType) => PLAN_META[plan]?.id ?? '';

export const getOfferColor = (plan: PlanType) =>
	PLAN_META[plan]?.color ?? '#a238ff';

/**
 * Prices to display for the plan cards (monthly steady-state).
 * Prefer renewal price (falls back to initial if needed).
 */
export const getOffersPrice = (
	plan: PlanType,
	offers: OffersAcquisitionResponse,
	selectedOffers: OffersAcquisitionType[],
) => {
	if (plan === 'annual-plan') {
		const yearly = getOffersYearlyInfoByPlan(plan, offers);
		return [yearly?.price_by_month ?? yearly?.initialPrice ?? '-'];
	}
	return selectedOffers.map((o) => getDisplayMonthlyPrice(o));
};

/**
 * Number of promo/trial months for a plan's offer(s).
 * - For `annual-plan`: duration of the tryAndBuy period of the first matching offer (0 if none).
 * - For other plans: for each matched offer, the promo duration in months from
 *   `getPromoMonths` (0 if the offer has no promo).
 */
export const getOfferPromoMonths = (
	plan: PlanType,
	selectedOffers: OffersAcquisitionType[],
) => {
	if (plan === 'annual-plan') {
		const o = selectedOffers[0];
		if (!o) return [0];
		const {tryAndBuy} = getPeriods(o);
		return [tryAndBuy?.duration ?? 0];
	}

	return selectedOffers.map((o) => getPromoMonths(o) ?? 0);
};

/**
 * These 3 helpers work on an already-selected list of offers (the result of
 * `selectOffers(plan, offers)`), instead of re-selecting internally. This
 * lets callers compute the selection once (e.g. memoized) and reuse it
 * across multiple helpers/renders instead of re-filtering every time.
 */
export const currentofferHasTryAndBuy = (
	selectedOffers: OffersAcquisitionType[],
) => selectedOffers.map((o) => hasTry(o))[0] ?? false;

export const getOfferTrialDays = (selectedOffers: OffersAcquisitionType[]) =>
	selectedOffers.map((o) => {
		const {tryAndBuy} = getPeriods(o);
		if (tryAndBuy?.duration && tryAndBuy.duration_type === 'D') {
			return tryAndBuy.duration;
		}
		return undefined;
	});

export const getPlanTitle = (selectedOffers: OffersAcquisitionType[]) => {
	return selectedOffers.map((o) => o.label);
};

export const getOffersInfoByPlan = (
	selectedOffers: OffersAcquisitionType[],
) => {
	const o = selectedOffers[0];
	if (!o) return undefined;
	const {initial, renewal, tryAndBuy} = getPeriods(o);

	return {
		initialPrice: initial?.price ?? renewal?.price ?? '-',
		offerId: o.offer_id,
		duration: tryAndBuy?.duration ?? undefined,
		label: o.label,
	};
};

export const getOfferPaymentUrlByPlan = (
	plan: PlanType,
	acqType: AcqType,
	selectedOffers: OffersAcquisitionType[],
) => {
	const base = selectedOffers[0]?.payment_journey_url;
	if (!base) return config.get('paymentHost');

	const baseURL = new URL(base);

	const acqSuffix = acqType === 'acq-2' ? 'acq-v2' : 'acq';
	const originParam = `${plan}-page-${acqSuffix}`;

	if (typeof window !== 'undefined') {
		const currentParams = new URLSearchParams(window.location.search);
		currentParams.forEach((value, key) => {
			if (key === 'cip') {
				baseURL.searchParams.delete('cip');
			}
			if (key !== 'origin') {
				// Strip trailing slash if present (fixes attribution issues with appended slashes)
				baseURL.searchParams.set(key, value.replace(/\/$/, ''));
			}
		});
	}
	baseURL.searchParams.set('origin', originParam);

	return baseURL.toString();
};

type OffersYearlyInfo = {
	initialPrice: string;
	price: string;
	price_by_month: string;
	months_duration: string;
	tnb_months_duration: string;
	label: string;
};

export const getOffersYearlyInfoByPlan = (
	plan: PlanType,
	offers: OffersAcquisitionResponse,
): OffersYearlyInfo | null => {
	const o = selectOffers(plan, offers, undefined, matchesPlan(plan))[0]; // just match id
	if (!o) return null;

	const {initial, renewal, tryAndBuy} = getPeriods(o);
	return {
		initialPrice: initial?.price ?? '-',
		price: renewal?.price ?? '-',
		price_by_month: initial?.price_by_month ?? '-',
		months_duration: String(
			getNumberOfMonthsFromPeriod({...initial} as any) ?? '-',
		),
		tnb_months_duration: String(tryAndBuy?.duration ?? '-'),
		label: o.label ?? 'premium',
	};
};

export const getCountryFromLocale = (locale: string, locales: string[]) => {
	if (!locales.includes(locale)) {
		throw new Error(
			`i18n: the locale "${locale}" is invalid or not supported.`,
		);
	}
	return getSSGCountryFromLocale(getLegacyLocaleFromLocale(locale));
};

export const fetchOffers = async ({
	locale,
	locales,
}: {
	locale: string;
	locales: string[];
}) => {
	const GEOIP = getCountryFromLocale(locale, locales);
	const response = await fetch(
		`${config.get(
			'deezerApiUrl',
		)}/ajax/gw-light.php?method=payment_getPlanCatalog&input=3&api_version=1.0&api_token=&cid=442313930&GEOIP=${GEOIP}`,
		{method: 'POST'},
	);
	const data = await response.json();
	return data;
};

export const fetchOffersFromCountry = async (country: string) => {
	const response = await fetch(
		`${config.get(
			'deezerApiUrl',
		)}/ajax/gw-light.php?method=payment_getPlanCatalog&input=3&api_version=1.0&api_token=&cid=442313930&GEOIP=${country}`,
		{method: 'POST'},
	);
	const data = await response.json();
	return data;
};

/* ---------------------------------- */
/* T&C text builder                  */
/* ---------------------------------- */

export const getTncByPlan = (
	plan: PlanType,
	offers: OffersAcquisitionResponse,
	t: any,
	selectedOffers: OffersAcquisitionType[],
): string[] => {
	if (plan === 'annual-plan') {
		const yearly = getOffersYearlyInfoByPlan(plan, offers);
		const monthsDur = +(yearly?.months_duration ?? 0);
		return [
			t('acqPages.text_ifcancelsubbeforeXmonthperiodnorefund', {
				sprintf: [yearly?.months_duration ?? '-'],
				count: monthsDur,
			}),
			t('acqPages.text_atendofXmonthperiodchargepriceXperyearunlessoptout', {
				sprintf: [yearly?.months_duration ?? '-', yearly?.price ?? '-'],
				count: monthsDur,
			}),
			t('acqPages.text_ifplanXsubscriberandupgradeimmediatechargeofpriceX', {
				sprintf: ['Deezer Premium', yearly?.initialPrice ?? '-'],
			}),
			t('acqPages.text_anycurrentmonthsubalreadypaidnotrefunded'),
			t('acqPages.text_nocombinationpossible'),
		];
	}

	const o = selectedOffers[0];
	if (!o) {
		return [t('acqPages.text_nocombinationpossible')];
	}

	const label = getPlanTitle(selectedOffers)[0] ?? 'Deezer Premium';
	const {initial, renewal} = getPeriods(o);
	const monthlyPrice = renewal?.price ?? initial?.price ?? '-';

	if (hasTry(o)) {
		const trial = getTrialInfo(o)!;
		const trialValue =
			trial.unit === 'D'
				? t('acqPages.days', {
						count: trial.duration,
						defaultValue: `${trial.duration}`,
					})
				: t('acqPages.months', {
						count: trial.duration,
						defaultValue: `${trial.duration}`,
					});

		return [
			t('acqPages.text_offerXmonthtrialserviceplanX', {
				sprintf: [trialValue, label],
				count: trial.duration,
			}),
			t('acqPages.text_autoconvtoplanXpriceX', {
				sprintf: [label, monthlyPrice],
			}),
			t('acqPages.text_offernonbindingmaycancelsubscriptionatanytime'),
			t('acqPages.text_newusersonly'),
			t('acqPages.text_onetimeonlyperuser'),
			t('acqPages.text_nocombinationpossible'),
		];
	}

	if (isDurationPromo(o)) {
		const info = getIntroPromoInfo(o);
		const introSpan =
			info.unit === 'M'
				? t('acqPages.months', {
						count: info.duration,
						defaultValue: `${info.duration}`,
					})
				: info.unit === 'D'
					? t('acqPages.days', {
							count: info.duration,
							defaultValue: `${info.duration}`,
						})
					: t('acqPages.years', {
							count: info.duration,
							defaultValue: `${info.duration}`,
						});

		return [
			t('acqPages.text_intropriceForPeriodThenPricePerMonth', {
				sprintf: [info.price ?? '-', introSpan, monthlyPrice],
			}),
			t('acqPages.text_offernonbindingmaycancelsubscriptionatanytime'),
			t('acqPages.text_newusersonly'),
			t('acqPages.text_onetimeonlyperuser'),
			t('acqPages.text_nocombinationpossible'),
		];
	}

	if (isIterationPromo(o)) {
		const info = getIntroPromoInfo(o);
		return [
			t('acqPages.text_trialtotheXserviceatXpriceforXmonths', {
				sprintf: [
					'Deezer Étudiant',
					info.price ?? '-',
					info.iteration,
					monthlyPrice,
				],
				count: info.iteration,
			}),
			t('acqPages.text_endofpromotionalofferchargedXprice', {
				sprintf: [monthlyPrice ?? '-'],
				count: info.price,
			}),
			t('acqPages.text_offernonbindingmaycancelsubscriptionatanytime'),
			t('acqPages.text_newusersonly'),
			t('acqPages.text_onetimeonlyperuser'),
			t('acqPages.text_nocombinationpossible'),
		];
	}

	return [
		t('acqPages.text_standardMonthlyNoCommitment', {
			sprintf: [label, monthlyPrice],
		}),
		t('acqPages.text_offernonbindingmaycancelsubscriptionatanytime'),
		t('acqPages.text_nocombinationpossible'),
	];
};

/* Tracking page-name from path -> single map instead of switch */

const TRACK_MAP: Record<string, string> = {
	'/offers/premium/acq/': 'premium-acq',
	'/offers/premium/acq-2/': 'premium-acq-v2',
	'/offers/family/acq/': 'family-acq',
	'/offers/family/acq-2/': 'family-acq-v2',
	'/offers/annual-plan/acq/': 'annual-plan-acq',
	'/offers/duo/acq/': 'duo-acq',
	'/offers/student/acq/': 'student-acq',
};

export const getTrackAcqPageName = (path: string) => TRACK_MAP[path] ?? '';

/* ---------------------------------- */
/* Countdown rule in one place        */
/* ---------------------------------- */

export const getExpiringPromoDeadlineMs = (
	plan: PlanType,
	acqType: AcqType,
	offers: OffersAcquisitionResponse | undefined,
): number | null => {
	if (!offers?.length) return null;

	const nowDt = now();
	const inSevenDaysMs = nowDt.getTime() + 7 * 24 * 60 * 60 * 1000;

	const eligible =
		(plan === 'premium' && (acqType === 'acq' || acqType === 'acq-2')) ||
		(plan === 'student' && acqType === 'acq') ||
		plan === 'duo' ||
		plan === 'family';

	if (!eligible) return null;

	const offersForPlan = selectOffers(plan, offers, undefined, (o) => {
		if (!matchesPlan(plan)(o) || !notBypassed(o)) return false;
		if (!hasActiveWindow(o, nowDt)) return false;

		if (plan === 'premium' || plan === 'student') {
			if (!isSpecial(o)) return false;
		} else {
			// accept either official specials or 2–3M "trial-like"
			if (!isSpecial(o) && !isTrial2M3M(o)) return false;
		}

		const rawEnd = (o as any)?.date_end?.date;
		const endMs = rawEnd ? new Date(rawEnd).getTime() : NaN;
		if (!isFinite(endMs)) return false;

		return endMs <= inSevenDaysMs;
	});

	const deadlineMs = offersForPlan
		.map((o) => {
			const rawEnd = (o as any)?.date_end?.date;
			return rawEnd ? new Date(rawEnd).getTime() : Infinity;
		})
		.filter((ms) => isFinite(ms))
		.sort((a, b) => a - b)[0];

	return isFinite(deadlineMs) ? deadlineMs : null;
};

export const getExpiringPromoDeadline = (
	plan: PlanType,
	acqType: AcqType,
	offers: OffersAcquisitionResponse | undefined,
): Date | null => {
	const ms = getExpiringPromoDeadlineMs(plan, acqType, offers);
	return ms ? new Date(ms) : null;
};

export const getLandingData = (
	selectedOffer: OffersAcquisitionType,
): Record<string, TranslationKey> => {
	if (!selectedOffer) return {};
	switch (selectedOffer.business_operation_code) {
		case '2026_student_ambassador':
			return {
				subtitle: 'acqPages.text_studentoffercostsoneeuro',
			};
		default:
			return {};
	}
};
